import { Link } from "react-router";
import { Download, Loader2, Paperclip } from "lucide-react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatCurrencyEUR, formatDate } from "@/lib/utils";
import { MdfStatusBadge } from "./mdf-status-badge";
import { MdfDecisionTimeline } from "./mdf-decision-timeline";
import { MdfCommentThread } from "./mdf-comment-thread";
import { useCancelMdf } from "./use-cancel-mdf";
import { useResubmitMdf } from "./use-resubmit-mdf";
import { useMdfAttachmentUrl } from "./use-mdf-attachment-url";
import type {
  MdfAttachment,
  MdfPartnerRequestDetail,
  MdfRequestStatus,
} from "./types";

// Read-only sibling of mdf-draft-edit-view. Rendered for every non-draft
// status: pending / approved / spent / declined / cancelled. Footer actions
// vary by status:
//   pending  → Cancel request
//   declined → Resubmit (clones into new draft, navigates to /portal/mdf/:newId)
//   else     → no actions
//
// Comments are permitted on pending/approved/declined per the backend
// service gate (services/mdf.ts::addPartnerComment).

const COMMENT_STATUSES: ReadonlySet<MdfRequestStatus> = new Set([
  "pending",
  "approved",
  "declined",
]);

export function MdfReadOnlyView({
  detail,
}: {
  detail: MdfPartnerRequestDetail;
}) {
  const { request, fund, attachments } = detail;
  const navigate = useNavigate();
  const cancel = useCancelMdf();
  const resubmit = useResubmitMdf();

  function handleCancel() {
    if (!confirm("Cancel this request? It will be marked as cancelled.")) return;
    cancel.mutate({ id: request.id });
  }

  function handleResubmit() {
    resubmit.mutate(
      { id: request.id },
      {
        onSuccess: ({ id }) => {
          toast.success("New draft started — pick up where you left off.");
          navigate(`/portal/mdf/${id}`, { replace: true });
        },
      },
    );
  }

  const showFooter =
    request.status === "pending" || request.status === "declined";
  const canComment = COMMENT_STATUSES.has(request.status);

  return (
    <div className="mx-auto max-w-2xl">
      <Link
        to="/portal/mdf"
        className="text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        ← Back to marketing funds
      </Link>

      <header className="mt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="truncate font-display text-2xl font-semibold tracking-tight">
              {request.campaign_name ?? "Untitled request"}
            </h1>
            {fund ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {fund.name}
                {request.campaign_type ? ` · ${request.campaign_type}` : ""}
              </p>
            ) : null}
          </div>
          <MdfStatusBadge status={request.status} />
        </div>
      </header>

      <section className="mt-6 grid gap-4 rounded-xl border bg-card p-5 sm:grid-cols-2">
        <DetailRow
          label="Amount"
          value={formatCurrencyEUR(request.amount_eur)}
          emphasis
        />
        <DetailRow
          label="Submitted"
          value={formatDate(request.created_at)}
        />
        {request.approved_at ? (
          <DetailRow label="Approved" value={formatDate(request.approved_at)} />
        ) : null}
        {request.spent_at ? (
          <DetailRow label="Marked spent" value={formatDate(request.spent_at)} />
        ) : null}
      </section>

      {request.description ? (
        <section className="mt-6">
          <h3 className="text-sm font-medium text-foreground">Description</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
            {request.description}
          </p>
        </section>
      ) : null}

      {attachments.length > 0 ? (
        <section className="mt-6">
          <h3 className="text-sm font-medium text-foreground">
            Supporting documents
          </h3>
          <ul className="mt-2 space-y-2">
            {attachments.map((a) => (
              <li key={a.id}>
                <AttachmentRow attachment={a} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="mt-6">
        <MdfDecisionTimeline
          history={request.status_history}
          currentStatus={request.status}
          createdAt={request.created_at}
          approvedAt={request.approved_at}
          declinedReason={request.declined_reason}
          spentAt={request.spent_at}
        />
      </div>

      <div className="mt-8">
        <MdfCommentThread requestId={request.id} canPost={canComment} />
      </div>

      {showFooter ? (
        <div className="mt-8 flex items-center justify-end gap-3 border-t pt-6">
          {request.status === "pending" ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={cancel.isPending}
              className="text-muted-foreground"
            >
              {cancel.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Cancel request
            </Button>
          ) : null}
          {request.status === "declined" ? (
            <Button
              type="button"
              onClick={handleResubmit}
              disabled={resubmit.isPending}
            >
              {resubmit.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Resubmit
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DetailRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p
        className={
          emphasis
            ? "mt-0.5 font-display text-lg font-semibold tabular-nums"
            : "mt-0.5 text-sm text-foreground"
        }
      >
        {value}
      </p>
    </div>
  );
}

function AttachmentRow({ attachment }: { attachment: MdfAttachment }) {
  const sign = useMdfAttachmentUrl();
  function handleDownload() {
    sign.mutate(
      { id: attachment.id },
      {
        onSuccess: ({ url }) => {
          window.open(url, "_blank", "noopener,noreferrer");
        },
      },
    );
  }
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border bg-card px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm">{attachment.file_name}</span>
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={sign.isPending}
        onClick={handleDownload}
      >
        {sign.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
}
